import { type Component, createApp, defineComponent, h, ref } from 'vue'
import { easeInOutQuad, generateID, nl2br, poorMansHTMLescape } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
import Button from '@ui/core/Button.vue'
import VSvg from '@/components/VSvg.vue'
import { Spinner, String, Modal, Markdown, SVG_LOAD, LANGUAGE, FRONTEND, type Frontend, useLogger, type UILabelOptional, type UILabel, labelFor } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import { loadSVG } from '@/lib/svg'
import { useProtocolStore } from '@/stores/protocol'

const log = useLogger('lib/ui', 'purple')

interface IndependentTextModalOptions {
	title?: string
	content: string
	ultrawide?: boolean
	withCloseButton?: boolean
	withOKButton?: boolean
	icon?: string
}

interface IndependentConfirmModalOptions {
	icon?: string
	title?: UILabelOptional
	question: UILabel
	positiveLabel?: UILabelOptional
	positiveColor?: string
	negativeLabel?: UILabelOptional
	negativeColor?: string
	neutralLabel?: UILabelOptional
	neutralColor?: string
	neutralAction?: () => void
}

const independentModalMixin = defineComponent({
	data() {
		return {
			unmount: null as any
		}
	},
	
	mounted() {
		(this.$refs.$modal as any).openModal()
	},
	
	methods: {
		close() {
			(this.$refs.$modal as any).closeModal()
		}
	},
})

const createIndependentTextModal = (options: IndependentTextModalOptions, resolve: Function) => defineComponent({
	name: 'IndependentTextModal',
	
	mixins: [
		independentModalMixin
	],
	
	render() {
		const buttonColor = options.icon == 'error' ? 'red' :
		                    options.icon == 'success' ? 'green' :
		                    options.icon == 'warning' ? 'orange' :
		                    undefined
		
		return h(Modal, {
			title: options.title,
			fullWidth: options.ultrawide ?? false,
			show_close_button: options.withCloseButton ?? false,
			icon: options.icon,
			ref: '$modal',
			full_page: options.ultrawide ?? false,
			close_on_overlay_click: false,
			
			onClose: () => {
				this.unmount?.()
				resolve(true)
			}
		}, {
			default: () => [
				h('div', {
					class: 'global-error-content',
					innerHTML: options.content,
					style: {
						lineHeight: '1.5'
					}
				})
			],
			
			right: () => options.withOKButton ? [
				h(Button, {
					color: buttonColor,
					type: 'raised',
					
					onClick: () => {
						this.close()
					}
				}, {
					default: () => [
						h('span', 'OK')
					]
				})
			] : []
		})
	}
})

const displayModal = (modal: Component, timer: number = 0) => {
	let app = createApp(modal)
	
	app.use((<any> window).__pinia)
	
	app.provide(SVG_LOAD, loadSVG)
	app.provide(LANGUAGE, ref(useProtocolStore().language))
	app.provide<Frontend>(FRONTEND, {
		getRootContainer(): HTMLElement {
			return document.querySelector('#independent-modals')!
		},
		
		getRootHeader(): HTMLElement | undefined {
			return undefined
		},
		
		getRootFooter(): HTMLElement | undefined {
			return undefined
		},
	})
	
	// Bootstrap necessary UI components
	app.component('VSvg', VSvg)
	app.component('acquisit-button', Button)
	app.component('acquisit-string', String)
	app.component('acquisit-spinner', Spinner)
	app.component('acquisit-modal', Modal)
	app.component('acquisit-markdown', Markdown)
	
	// Run the instance, open the modal
	// The element can't be named "#global-modals", because Vite replaces *any* occurrence of "global" with "window". Boo.
	const $modalsContainer = document.querySelector('#independent-modals')
	
	if (!$modalsContainer) {
		log.always.errorFrom('displayModal', 'Could not find modals container')
		return false
	}
	
	const $appHost = document.createElement('div')
	$appHost.setAttribute('id', generateID('modal-'))
	$modalsContainer.appendChild($appHost)
	
	const instance = app.mount($appHost)
	
	if (!instance?.$refs?.$modal) {
		$modalsContainer.removeChild($appHost)
		return false
	}
	
	(instance.$data as any).unmount = () => {
		app.unmount()
		$modalsContainer.removeChild($appHost)
	}
	
	if (timer > 0) {
		setTimeout(() => {
			(instance.$refs.$modal as any).close()
		}, timer)
	}
	
	return app
}

export const errorMessage = (
	content: string,
	title?: string,
	timer: number = 0,
	withOKButton = true,
	withCloseButton = false,
	ultrawide: boolean = false,
) => new Promise(async (resolve) => {
	const component = createIndependentTextModal({
		title,
		content,
		withCloseButton,
		withOKButton,
		ultrawide,
		// icon: 'info/error'
	}, resolve)
	
	if (!displayModal(component, timer ?? 0)) {
		log.always.error(content, title)
		throw new Error('Could not find error modal')
	}
})

const createIndependentConfirmModal = (options: IndependentConfirmModalOptions, resolve: Function) => defineComponent({
	name: 'IndependentConfirmModal',
	
	mixins: [
		independentModalMixin
	],
	
	methods: {
		cancel() {
			resolve(false)
			this.close()
		},
		
		confirm() {
			resolve(true)
			this.close()
		}
	},
	
	render() {
		const positive = () => [
			h(Button, {
				type: 'raised',
				label: options.positiveLabel,
				color: options.positiveColor,
				
				onClick: () => {
					this.confirm()
				}
			})
		]
		
		const negative = () => [
			h(Button, {
				type: 'flat',
				label: options.negativeLabel || 'Avbryt',
				color: options.negativeColor,
				
				onClick: () => {
					this.cancel()
				}
			}),
		]
		
		const neutral = () => {
			if (options.neutralLabel && options.neutralAction) {
				return [
					h(Button, {
						type: 'flat',
						color: options.neutralColor,
						label: options.neutralLabel,
						onClick: () => {
							options.neutralAction?.()
							resolve(null)
							this.close()
						}
					})
				]
			}
			
			return []
		}
		
		// FIXME: Implement neutral
		
		// without as any: Types of property 'ref' are incompatible.
		// I have no clue why because it works in the functions above.
		return h(Modal as any, {
			title: options.title,
			show_close_button: false,
			icon: options.icon,
			ref: '$modal',
			close_on_overlay_click: false,
			
			onClose: () => {
				this.unmount?.()
			}
		}, {
			default: () => [
				h('p', {
					innerHTML: nl2br(poorMansHTMLescape(labelFor(options.question, undefined, ''))),
					style: {}
				})
			],
			
			left: () => negative(),
			right: () => positive(),
		})
	}
})

export const confirmMessage = async (options: IndependentConfirmModalOptions): Promise<boolean> => new Promise(resolve => {
	const component = createIndependentConfirmModal(options, resolve)
	
	if (!displayModal(component)) {
		log.always.warnFrom('confirmMessage', 'Could not find error modal')
		const question = labelFor(options.question, undefined, '')
		
		resolve(window.confirm(question) ?? false)
	}
})

export const scrollElementTo = (element: HTMLElement, to: number, direction: 'top'|'left' = 'top', duration: number = 300) => {
	let scrollProperty: string
	
	switch (direction) {
		case 'top':
			scrollProperty = 'scrollTop'
			break
		
		case 'left':
			scrollProperty = 'scrollLeft'
			break
		
		default:
			throw new Error('Unsupported direction: ' + direction)
	}
	
	let start = element[scrollProperty],
		change = to - start,
		currentTime = 0,
		increment = 20
	
	if (duration === 0) {
		element[scrollProperty] = to
	} else {
		let animateScroll = () => {
			currentTime += increment
			element[scrollProperty] = easeInOutQuad(currentTime, start, change, duration)
			
			if (currentTime < duration) {
				setTimeout(animateScroll, increment)
			}
		}
		
		animateScroll()
	}
}